// /tenants — cross-tenant list. Filter bar + table; row click → detail.
//
// Filters live in URL query params so the operator can deep-link a
// filtered view (e.g. `?plan=pro&freeze=frozen` for "frozen pros, fix
// today"). The TanStack Query cache is keyed on the filter shape so
// stale data doesn't bleed across filter changes.
//
// No client-side TanStack Table for P1 — straight HTML over the shadcn
// `Table` component matches the customer dashboard convention and
// keeps the bundle smaller. Sort/bulk-select land when P3 needs them.

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useApiClient } from '../../lib/providers.js';
import {
  TENANTS_LIST_QUERY_KEY,
  tenantsApi,
  type ListTenantsParams,
  type TenantFreezeFilter,
  type TenantLifecycleFilter,
  type TenantPlanFilter,
  type TenantRow,
} from '../../lib/api/tenants.js';
import { Badge } from '../../components/ui/badge.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';

const PLAN_VALUES: TenantPlanFilter[] = ['all', 'free', 'starter', 'pro'];
const FREEZE_VALUES: TenantFreezeFilter[] = ['all', 'active', 'frozen'];
const LIFECYCLE_VALUES: TenantLifecycleFilter[] = ['live', 'soft_deleted', 'all'];

export function TenantsListPage() {
  const client = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');

  const filters: ListTenantsParams = useMemo(() => {
    const search = searchParams.get('search');
    return {
      ...(search ? { search } : {}),
      plan: (searchParams.get('plan') as TenantPlanFilter | null) ?? 'all',
      freeze: (searchParams.get('freeze') as TenantFreezeFilter | null) ?? 'all',
      lifecycle: (searchParams.get('lifecycle') as TenantLifecycleFilter | null) ?? 'live',
      limit: 100,
    };
  }, [searchParams]);

  const query = useQuery({
    queryKey: [...TENANTS_LIST_QUERY_KEY, filters] as const,
    queryFn: () => tenantsApi.list(client, filters),
  });

  function setFilter(key: string, value: string | null): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value.length === 0) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  function commitSearch(e: React.FormEvent): void {
    e.preventDefault();
    setFilter('search', searchInput.trim().length > 0 ? searchInput.trim() : null);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenants"
        description="Every tenant on the platform. Click a row to drill into usage, subscription state, and operator actions."
      />

      {/* Filter bar */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-end">
        <form onSubmit={commitSearch} className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="tenant-search" className="text-xs text-muted-foreground">
            Search by name or id
          </Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="tenant-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onBlur={commitSearch}
              placeholder="Acme Inc, or full uuid"
              className="pl-8"
            />
          </div>
        </form>
        <FilterSelect
          label="Plan"
          value={filters.plan ?? 'all'}
          values={PLAN_VALUES}
          onChange={(v) => {
            setFilter('plan', v === 'all' ? null : v);
          }}
        />
        <FilterSelect
          label="Freeze"
          value={filters.freeze ?? 'all'}
          values={FREEZE_VALUES}
          onChange={(v) => {
            setFilter('freeze', v === 'all' ? null : v);
          }}
        />
        <FilterSelect
          label="Lifecycle"
          value={filters.lifecycle ?? 'live'}
          values={LIFECYCLE_VALUES}
          onChange={(v) => {
            setFilter('lifecycle', v === 'live' ? null : v);
          }}
        />
      </div>

      {/* Body */}
      {query.isPending ? (
        <TenantsTableSkeleton />
      ) : query.isError ? (
        <ErrorRetry
          title="Failed to load tenants"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.rows.length === 0 ? (
        <EmptyState
          title="No tenants match the current filters"
          description="Loosen the filters or clear them to see every tenant."
        />
      ) : (
        <TenantsTable rows={query.data.rows} />
      )}
    </div>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: T;
  values: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 sm:w-40">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={value}
        onChange={(e) => {
          onChange(e.target.value as T);
        }}
      >
        {values.map((v) => (
          <option key={v} value={v}>
            {labelFor(v)}
          </option>
        ))}
      </Select>
    </div>
  );
}

function labelFor(value: string): string {
  switch (value) {
    case 'all':
      return 'All';
    case 'free':
      return 'Free';
    case 'starter':
      return 'Starter';
    case 'pro':
      return 'Pro';
    case 'active':
      return 'Active';
    case 'frozen':
      return 'Frozen';
    case 'live':
      return 'Live';
    case 'soft_deleted':
      return 'Soft-deleted';
    default:
      return value;
  }
}

function TenantsTable({ rows }: { rows: readonly TenantRow[] }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead className="text-right">Cost (30d)</TableHead>
            <TableHead>Last activity</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} className="cursor-pointer hover:bg-muted/40">
              <TableCell className="max-w-[280px]">
                <Link to={`/tenants/${row.id}`} className="block">
                  <div className="truncate font-medium">{row.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {row.email ?? '—'}
                  </div>
                </Link>
              </TableCell>
              <TableCell>
                <Link to={`/tenants/${row.id}`} className="block">
                  <PlanBadge code={row.planCode} />
                </Link>
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                <Link to={`/tenants/${row.id}`} className="block">
                  {formatUsd(row.costLast30dUsd)}
                </Link>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                <Link to={`/tenants/${row.id}`} className="block">
                  {row.lastActiveAt ? formatDateTime(row.lastActiveAt) : '—'}
                </Link>
              </TableCell>
              <TableCell>
                <Link to={`/tenants/${row.id}`} className="block">
                  <StateBadges row={row} />
                </Link>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                <Link to={`/tenants/${row.id}`} className="block">
                  {formatDate(row.createdAt)}
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PlanBadge({ code }: { code: string | null }) {
  if (!code) return <Badge variant="outline">unknown</Badge>;
  const variant: 'default' | 'secondary' | 'outline' =
    code === 'pro' ? 'default' : code === 'starter' ? 'secondary' : 'outline';
  return <Badge variant={variant}>{code}</Badge>;
}

function StateBadges({ row }: { row: TenantRow }) {
  const items: { key: string; node: React.ReactNode }[] = [];
  if (row.frozenAt) {
    items.push({
      key: 'frozen',
      node: (
        <Badge variant="destructive" title={row.frozenReason ?? 'Frozen'}>
          frozen
        </Badge>
      ),
    });
  }
  if (row.deletedAt) {
    items.push({
      key: 'deleted',
      node: <Badge variant="secondary">soft-deleted</Badge>,
    });
  }
  if (items.length === 0) {
    return <Badge variant="outline">live</Badge>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((i) => (
        <span key={i.key}>{i.node}</span>
      ))}
    </div>
  );
}

function TenantsTableSkeleton() {
  return (
    <div className="space-y-2 rounded-lg border bg-card p-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
